import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaEye,
  FaMousePointer,
  FaChrome,
  FaFirefox,
  FaSafari,
  FaEdge
} from 'react-icons/fa';

const HTMLLesson6 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const sections = [
    {
      title: "Viewing HTML Source Code",
      content: "Learn how to examine the underlying HTML code of any webpage to understand how it's built",
      steps: [
        "Right-click anywhere on the webpage",
        "Select 'View Page Source' (Chrome) or 'View Source' (other browsers)",
        "A new window/tab opens showing the raw HTML code",
        "Explore the structure and learn from real websites"
      ],
      icon: <FaEye className="section-icon" />,
      color: "#2563eb"
    },
    {
      title: "Inspecting HTML Elements", 
      content: "Use browser developer tools to examine and edit HTML elements in real-time",
      steps: [
        "Right-click on any specific element",
        "Choose 'Inspect' or 'Inspect Element'",
        "Developer tools open showing the element's HTML and CSS",
        "Edit code on-the-fly to see immediate changes"
      ],
      icon: <FaMousePointer className="section-icon" />,
      color: "#1d4ed8"
    }
  ];

  const browserMethods = [
    {
      browser: "Google Chrome",
      icon: <FaChrome className="browser-icon" />,
      viewSource: "Right-click → 'View Page Source'",
      inspectElement: "Right-click → 'Inspect'",
      shortcut: "Ctrl+U (Windows) / Cmd+U (Mac)"
    },
    {
      browser: "Mozilla Firefox",
      icon: <FaFirefox className="browser-icon" />,
      viewSource: "Right-click → 'View Page Source'",
      inspectElement: "Right-click → 'Inspect Element'",
      shortcut: "Ctrl+U (Windows) / Cmd+U (Mac)"
    },
    {
      browser: "Safari",
      icon: <FaSafari className="browser-icon" />,
      viewSource: "Right-click → 'Show Page Source'",
      inspectElement: "Right-click → 'Inspect Element'",
      shortcut: "Cmd+Option+U (Mac)"
    },
    {
      browser: "Microsoft Edge",
      icon: <FaEdge className="browser-icon" />,
      viewSource: "Right-click → 'View Source'",
      inspectElement: "Right-click → 'Inspect'",
      shortcut: "Ctrl+U (Windows)"
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 2;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 6 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">How to View HTML Source Code</h1>
              <p className="html-lesson-subtitle">
                Learn to inspect and analyze any webpage's HTML structure like a professional developer
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Introduction Section */}
          <div 
            className="html-intro-section"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-intro-content">
              <h2>Ever wondered how websites are built?</h2>
              <p>Every webpage you visit is built with HTML, and you can peek behind the curtain to see exactly how it's made. This is one of the most powerful learning tools for web developers!</p>
              
              <div className="html-intro-tip">
                <FaCode className="html-tip-icon" />
                <p><strong>Pro Tip:</strong> Use these techniques to learn from professional websites and improve your own coding skills.</p>
              </div>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Method ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-steps">
                      <h4>Step-by-Step Guide:</h4>
                      <ol>
                        {section.steps.map((step, idx) => (
                          <li key={idx}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </div>
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Demonstration
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Browser Comparison Section */}
          <div 
            className="html-browsers-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-browsers-header">
              <div className="html-code-badge">
                <FaChrome className="html-badge-icon" />
                Browser Comparison
              </div>
              <h3 className="html-browsers-title">
                Methods Across Different Browsers
              </h3>
            </div>
            
            <div className="html-browsers-grid">
              {browserMethods.map((browser, index) => (
                <div key={index} className="html-browser-card">
                  <div className="html-browser-header">
                    {browser.icon}
                    <h4>{browser.browser}</h4>
                  </div>
                  
                  <div className="html-browser-methods">
                    <div className="html-method">
                      <strong>View Source:</strong>
                      <span>{browser.viewSource}</span>
                    </div>
                    <div className="html-method">
                      <strong>Inspect Element:</strong>
                      <span>{browser.inspectElement}</span>
                    </div>
                    <div className="html-method">
                      <strong>Keyboard Shortcut:</strong>
                      <code>{browser.shortcut}</code>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Practical Exercise Section */}
          <div 
            className="html-exercise-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-exercise-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Practice Exercise
              </div>
              <h3 className="html-exercise-title">
                Try It Yourself!
              </h3>
            </div>
            
            <div className="html-exercise-content">
              <div className="html-exercise-steps">
                <h4>Learning Exercise:</h4>
                <ol>
                  <li>Visit your favorite website (like Google, YouTube, or Facebook)</li>
                  <li>Right-click and choose "View Page Source"</li>
                  <li>Look for familiar HTML tags you've learned</li>
                  <li>Try inspecting specific elements like buttons or images</li>
                  <li>Notice how professional websites structure their code</li>
                </ol>
              </div>
              
              <div className="html-exercise-tips">
                <h4>What to Look For:</h4>
                <ul>
                  <li><code>&lt;head&gt;</code> section with metadata</li>
                  <li><code>&lt;body&gt;</code> structure and content</li>
                  <li>How headings (<code>&lt;h1&gt;</code> to <code>&lt;h6&gt;</code>) are used</li>
                  <li>Paragraphs (<code>&lt;p&gt;</code>) and links (<code>&lt;a&gt;</code>)</li>
                  <li>Image tags (<code>&lt;img&gt;</code>) and their attributes</li>
                </ul>
              </div>
            </div>
            
            <div className="html-exercise-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Live Demonstration
              </a>
            </div>
          </div>

          {/* Benefits Section */}
          <div 
            className="html-benefits-section"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-benefits-header">
              <h3>Why This Skill is Essential</h3>
            </div>
            
            <div className="html-benefits-grid">
              <div className="html-benefit-card">
                <div className="html-benefit-icon" style={{ backgroundColor: '#10b981' }}>
                  <FaEye />
                </div>
                <h4>Learn from Experts</h4>
                <p>Study how professional developers structure their code and solve problems</p>
              </div>
              
              <div className="html-benefit-card">
                <div className="html-benefit-icon" style={{ backgroundColor: '#3b82f6' }}>
                  <FaMousePointer />
                </div>
                <h4>Debug Efficiently</h4>
                <p>Quickly identify and fix issues in your own HTML code</p>
              </div>
              
              <div className="html-benefit-card">
                <div className="html-benefit-icon" style={{ backgroundColor: '#8b5cf6' }}>
                  <FaCode />
                </div>
                <h4>Understand Web Structure</h4>
                <p>Gain deeper insight into how websites are built and organized</p>
              </div>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 4)}
          >
            <div className="html-action-header">
              <h3>Ready to explore behind the scenes?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned how to inspect any webpage's HTML source code. This is a fundamental skill for every web developer!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-comments" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Want to see it in action? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson6;